<template>
  <v-list class="py-0 d-flex flex-column gap-4">
    <iterable-sidebar-item
      v-for="(item, index) in generalMenu"
      :key="`general-menu-${index}`"
      :menu-item="item"
    />
    <divider />
    <personal-manager-card v-if="personalManagerIsShown" is-compact />
    <widget-banner
      v-show="!isMiniSidebarShown"
      :widget-html="sidebarWidget?.code"
      :class="{ 'mt-3': personalManagerIsShown }"
    />
    <divider v-if="sidebarWidget?.code || personalManagerIsShown" />
    <iterable-sidebar-item
      v-for="(item, index) in helpMenu"
      :key="`help-menu-${index}`"
      :menu-item="item"
    />
    <divider />
  </v-list>
</template>

<script>
  import { mapState } from 'pinia'
  import { useSettingsStore } from '@/store/settings/settings'
  import IterableSidebarItem from '@/components/main/Sidebar/IterableSidebarItem.vue'
  import WidgetBanner from '@/components/main/Sidebar/WidgetBanner.vue'
  import routeNames from '@/types/route-names'
  import PersonalManagerCard from '@/components/PersonalManagerCard/Index.vue'
  import Divider from '@/components/main/Sidebar/Divider.vue'

  export default {
    name: 'MenuBar',
    components: {
      Divider,
      PersonalManagerCard,
      IterableSidebarItem,
      WidgetBanner
    },
    computed: {
      ...mapState(useSettingsStore, ['isMiniSidebarShown', 'adNetwork', 'locale']),
      sidebarWidget() {
        const { sidebarWidget } = this.adNetwork.settings.adsSpots
        if (sidebarWidget.locale_data) {
          const sidebarWidgetByLocale = sidebarWidget.locale_data.find(
            (data) => data.locale === this.locale || data.locale === 'en'
          )
          return sidebarWidgetByLocale ?? sidebarWidget.locale_data[0]
        }
        return null
      },
      personalManagerIsShown() {
        return (
          this.$auth.user?.account_manager || this.adNetwork.settings.defaultAccountManager.name
        )
      },
      generalMenu() {
        const menu = [
          {
            text: this.$t('main.sidebar.dashboard'),
            page: routeNames.DASHBOARD,
            activeOn: [routeNames.DASHBOARD],
            icon: '$chart-circle'
          },
          {
            text: this.$t('main.sidebar.campaigns'),
            page: routeNames.CAMPAIGNS,
            activeOn: [routeNames.CAMPAIGNS],
            icon: '$rocket'
          },
          {
            text: this.$t('main.sidebar.ads'),
            page: routeNames.ADS,
            activeOn: [routeNames.ADS],
            icon: '$megaphone'
          },
          {
            text: this.$t('main.sidebar.statistics'),
            page: routeNames.CAMPAIGN_STATISTICS,
            activeOn: [routeNames.CAMPAIGN_STATISTICS],
            icon: '$chart-bars'
          },
          {
            text: this.$t('main.sidebar.traffic_chart'),
            page: routeNames.TRAFFIC_CHART,
            activeOn: [routeNames.TRAFFIC_CHART],
            icon: '$file-text'
          },
          {
            text: this.$t('main.sidebar.tracking'),
            page: routeNames.TRACKING,
            activeOn: [routeNames.TRACKING],
            icon: '$chart-search'
          },
          {
            text: this.$t('main.sidebar.add_funds'),
            page: routeNames.ADD_FUNDS,
            activeOn: [routeNames.ADD_FUNDS],
            icon: '$credit-card'
          },
          {
            text: this.$t('main.sidebar.finances'),
            page: routeNames.FINANCES,
            activeOn: [routeNames.FINANCES],
            icon: '$shield-dollar'
          },
          {
            text: this.$t('main.sidebar.marketplace'),
            page: routeNames.MARKETPLACE,
            activeOn: [routeNames.MARKETPLACE],
            icon: '$store'
          },
          {
            text: this.$t('main.sidebar.traffic_dsp'),
            page: routeNames.PROVIDERS,
            activeOn: [routeNames.PROVIDERS],
            icon: '$earth-search'
          }
        ]
        return this.filterMenu(menu)
      },
      helpMenu() {
        const menu = [
          {
            text: this.$t('main.sidebar.api'),
            page: routeNames.API,
            activeOn: [routeNames.API],
            icon: '$link'
          },
          {
            text: this.$t('main.sidebar.referral_program'),
            page: routeNames.PERSONAL,
            activeOn: [routeNames.PERSONAL],
            icon: '$user-dollar'
          },
          {
            text: this.$t('main.sidebar.rtb'),
            page: routeNames.RTB,
            activeOn: [
              routeNames.RTB,
              'RtbEndpoints',
              'RtbCreateEndpoint',
              'RtbEditEndpoint',
              'RtbNetwork',
              'RtbCreateNetwork'
            ],
            icon: '$rtb'
          },
          {
            text: this.$t('main.sidebar.rtb_statistics'),
            page: routeNames.RTB_STATISTICS,
            activeOn: [routeNames.RTB_STATISTICS],
            icon: '$chart-bars'
          },
          {
            text: this.$t('main.sidebar.ssp_statistics'),
            page: routeNames.SSP_STATISTICS,
            activeOn: [routeNames.SSP_STATISTICS],
            icon: '$chart-bars'
          },
          {
            text: this.$t('main.sidebar.blog'),
            href: this.adNetwork.settings.helpUrls.blog,
            activeOn: [],
            icon: '$book-open',
            isShown: !!this.adNetwork.settings.helpUrls.blog
          }
        ]
        return this.filterMenu(menu)
      },
      routes() {
        return this.$router.options.routes
      }
    },
    methods: {
      filterMenu(menu) {
        const userPermissions = this.$auth.user?.permissions ?? []
        return menu.filter((menuItem) => {
          if (!menuItem.page) return menuItem.isShown

          const foundRoute = this.routes.find((route) => route.name === menuItem.page)

          if (!foundRoute) return false

          if (
            foundRoute.meta.permissions?.length &&
            !foundRoute.meta.permissions.every((permission) => userPermissions.includes(permission))
          ) {
            return false
          }

          if (foundRoute.meta.section) {
            return this.adNetwork.guardSections.some(({ name }) => name === foundRoute.meta.section)
          }
          return true
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .gap-4 {
    gap: 4px;
  }
</style>
